/**
 * MASTER PERMISSION REGISTRY - Single Source of Truth (Frontend)
 * 
 * This file mirrors ProgramPermissionRegistry.cs in the backend.
 * All program-level permissions MUST be registered here.
 * 
 * This prevents RBAC drift where features are built but permissions aren't registered.
 */

// ═══════════════════════════════════════════════════════════════
// ACTION KEYS - Mirrors backend ProgramPermissionRegistry.ActionKeys
// ═══════════════════════════════════════════════════════════════

export const PROGRAM_ACTION_KEYS = {
  // Patient Module
  PATIENT_VIEW: 'PATIENT_VIEW',
  PATIENT_CREATE: 'PATIENT_CREATE',
  PATIENT_EDIT: 'PATIENT_EDIT',
  PATIENT_DELETE: 'PATIENT_DELETE',
  PATIENT_VIEW_ARCHIVED: 'PATIENT_VIEW_ARCHIVED',
  PATIENT_VIEW_DELETED: 'PATIENT_VIEW_DELETED',
  PATIENT_RESTORE_DELETED: 'PATIENT_RESTORE_DELETED',
  PATIENT_EXPORT: 'PATIENT_EXPORT',
  PATIENT_APPROVAL_EMAIL_RESEND: 'PATIENT_APPROVAL_EMAIL_RESEND',

  // Patient 360 Module
  PATIENT_360_VIEW: 'PATIENT_360_VIEW',
  PATIENT_360_VIEW_CASES: 'PATIENT_360_VIEW_CASES',
  PATIENT_360_VIEW_CALL_LOGS: 'PATIENT_360_VIEW_CALL_LOGS',
  PATIENT_360_VIEW_ADVERSE_EVENTS: 'PATIENT_360_VIEW_ADVERSE_EVENTS',
  PATIENT_360_VIEW_DOCUMENTS: 'PATIENT_360_VIEW_DOCUMENTS',
  PATIENT_360_VIEW_TIMELINE: 'PATIENT_360_VIEW_TIMELINE',

  // Hospital Module
  HOSPITAL_VIEW: 'HOSPITAL_VIEW',
  HOSPITAL_CREATE: 'HOSPITAL_CREATE',
  HOSPITAL_EDIT: 'HOSPITAL_EDIT',
  HOSPITAL_DELETE: 'HOSPITAL_DELETE',
  HOSPITAL_EXPORT: 'HOSPITAL_EXPORT',

  // HCP Module
  HCP_VIEW: 'HCP_VIEW',
  HCP_CREATE: 'HCP_CREATE',
  HCP_EDIT: 'HCP_EDIT',
  HCP_DELETE: 'HCP_DELETE',
  HCP_EXPORT: 'HCP_EXPORT',

  // Assistance Case Module
  ASSISTANCE_CASE_VIEW: 'ASSISTANCE_CASE_VIEW',
  ASSISTANCE_CASE_CREATE: 'ASSISTANCE_CASE_CREATE',
  ASSISTANCE_CASE_EDIT: 'ASSISTANCE_CASE_EDIT',
  ASSISTANCE_CASE_CANCEL: 'ASSISTANCE_CASE_CANCEL',
  ASSISTANCE_CASE_APPROVE: 'ASSISTANCE_CASE_APPROVE',
  ASSISTANCE_CASE_REJECT: 'ASSISTANCE_CASE_REJECT',
  ASSISTANCE_CASE_EXPORT: 'ASSISTANCE_CASE_EXPORT',

  // Assistance Claim Module
  ASSISTANCE_CLAIM_VIEW: 'ASSISTANCE_CLAIM_VIEW',
  ASSISTANCE_CLAIM_CREATE: 'ASSISTANCE_CLAIM_CREATE',
  ASSISTANCE_CLAIM_EDIT: 'ASSISTANCE_CLAIM_EDIT',
  ASSISTANCE_CLAIM_SUBMIT: 'ASSISTANCE_CLAIM_SUBMIT',
  ASSISTANCE_CLAIM_APPROVE: 'ASSISTANCE_CLAIM_APPROVE',
  ASSISTANCE_CLAIM_REJECT: 'ASSISTANCE_CLAIM_REJECT',
  ASSISTANCE_CLAIM_EXPORT: 'ASSISTANCE_CLAIM_EXPORT',

  // Call Log Module
  CALL_LOG_VIEW: 'CALL_LOG_VIEW',
  CALL_LOG_CREATE: 'CALL_LOG_CREATE',
  CALL_LOG_EDIT: 'CALL_LOG_EDIT',
  CALL_LOG_DELETE: 'CALL_LOG_DELETE',
  CALL_LOG_ANALYTICS_VIEW: 'CALL_LOG_ANALYTICS_VIEW',
  CALL_LOG_FOLLOWUP_WORKLIST_VIEW: 'CALL_LOG_FOLLOWUP_WORKLIST_VIEW',
  CALL_LOG_EXPORT: 'CALL_LOG_EXPORT',
  // Entity-specific call log actions
  CALL_LOG_CREATE_PATIENT: 'CALL_LOG_CREATE_PATIENT',
  CALL_LOG_CREATE_HCP: 'CALL_LOG_CREATE_HCP',
  CALL_LOG_CREATE_HOSPITAL: 'CALL_LOG_CREATE_HOSPITAL',
  CALL_LOG_CREATE_PHARMA: 'CALL_LOG_CREATE_PHARMA',

  // Adverse Event Module
  ADVERSE_EVENT_VIEW: 'ADVERSE_EVENT_VIEW',
  ADVERSE_EVENT_CREATE: 'ADVERSE_EVENT_CREATE',
  ADVERSE_EVENT_EDIT: 'ADVERSE_EVENT_EDIT',
  ADVERSE_EVENT_SUBMIT: 'ADVERSE_EVENT_SUBMIT',
  ADVERSE_EVENT_REVIEW: 'ADVERSE_EVENT_REVIEW',
  ADVERSE_EVENT_EXPORT: 'ADVERSE_EVENT_EXPORT',

  // Workflow Module
  WORKFLOW_VIEW: 'WORKFLOW_VIEW',
  WORKFLOW_CREATE: 'WORKFLOW_CREATE',
  WORKFLOW_EDIT: 'WORKFLOW_EDIT',
  WORKFLOW_ACTIVATE: 'WORKFLOW_ACTIVATE',
  WORKFLOW_DEACTIVATE: 'WORKFLOW_DEACTIVATE',
  WORKFLOW_CLONE: 'WORKFLOW_CLONE',
  WORKFLOW_TRANSITION: 'WORKFLOW_TRANSITION',
  WORKFLOW_APPROVE: 'WORKFLOW_APPROVE',

  // Form Module
  FORM_VIEW: 'FORM_VIEW',
  FORM_CREATE: 'FORM_CREATE',
  FORM_EDIT: 'FORM_EDIT',
  FORM_PUBLISH: 'FORM_PUBLISH',
  FORM_ARCHIVE: 'FORM_ARCHIVE',
  FORM_DELETE: 'FORM_DELETE',
  FORM_CLONE: 'FORM_CLONE',
  FORM_FILL: 'FORM_FILL',
  FORM_SUBMIT: 'FORM_SUBMIT',

  // Report Module
  REPORT_VIEW: 'REPORT_VIEW',
  REPORT_EXPORT: 'REPORT_EXPORT',
  REPORT_SCHEDULE: 'REPORT_SCHEDULE',

  // Audit Module
  AUDIT_VIEW: 'AUDIT_VIEW',
  AUDIT_EXPORT: 'AUDIT_EXPORT',

  // Notification Module
  NOTIFICATION_VIEW: 'NOTIFICATION_VIEW',
  NOTIFICATION_CREATE: 'NOTIFICATION_CREATE',
  NOTIFICATION_EDIT: 'NOTIFICATION_EDIT',
  NOTIFICATION_DELETE: 'NOTIFICATION_DELETE',

  // Financial Module
  FINANCIAL_VIEW: 'FINANCIAL_VIEW',
  FINANCIAL_MANAGE: 'FINANCIAL_MANAGE',
  FINANCIAL_APPROVE: 'FINANCIAL_APPROVE',

  // Utilization Module
  UTILIZATION_VIEW: 'UTILIZATION_VIEW',
  UTILIZATION_CREATE: 'UTILIZATION_CREATE',
  UTILIZATION_EDIT: 'UTILIZATION_EDIT',
  UTILIZATION_SUBMIT: 'UTILIZATION_SUBMIT',
  UTILIZATION_APPROVE: 'UTILIZATION_APPROVE',
  UTILIZATION_REJECT: 'UTILIZATION_REJECT',

  // Automation Rules Module
  AUTOMATION_RULE_VIEW: 'AUTOMATION_RULE_VIEW',
  AUTOMATION_RULE_CREATE: 'AUTOMATION_RULE_CREATE',
  AUTOMATION_RULE_EDIT: 'AUTOMATION_RULE_EDIT',
  AUTOMATION_RULE_ACTIVATE: 'AUTOMATION_RULE_ACTIVATE',
  AUTOMATION_RULE_DEACTIVATE: 'AUTOMATION_RULE_DEACTIVATE',

  // User & Role Management
  ROLE_VIEW: 'ROLE_VIEW',
  ROLE_CREATE: 'ROLE_CREATE',
  ROLE_EDIT: 'ROLE_EDIT',
  ROLE_DELETE: 'ROLE_DELETE',
  USER_VIEW: 'USER_VIEW',
  USER_ASSIGN: 'USER_ASSIGN',
  USER_UNASSIGN: 'USER_UNASSIGN',

  // Workspace Module
  WORKSPACE_VIEW: 'WORKSPACE_VIEW',
  WORKSPACE_VIEW_CASES: 'WORKSPACE_VIEW_CASES',
  WORKSPACE_VIEW_ORDERS: 'WORKSPACE_VIEW_ORDERS',
  WORKSPACE_VIEW_APPROVALS: 'WORKSPACE_VIEW_APPROVALS',
  WORKSPACE_VIEW_ADVERSE_EVENTS: 'WORKSPACE_VIEW_ADVERSE_EVENTS',
  WORKSPACE_VIEW_FOLLOWUPS: 'WORKSPACE_VIEW_FOLLOWUPS',
  WORKSPACE_VIEW_CLAIMS: 'WORKSPACE_VIEW_CLAIMS',

  // Patient Cycle Request Module (mirrors backend ProgramPermissionRegistry.ActionKeys)
  CYCLE_REQUEST_CREATE_OWN: 'CYCLE_REQUEST_CREATE_OWN',
  CYCLE_REQUEST_CREATE: 'CYCLE_REQUEST_CREATE',
  CYCLE_REQUEST_VIEW_OWN: 'CYCLE_REQUEST_VIEW_OWN',
  CYCLE_REQUEST_VIEW: 'CYCLE_REQUEST_VIEW',
  CYCLE_REQUEST_REVIEW: 'CYCLE_REQUEST_REVIEW',
  CYCLE_REQUEST_APPROVE: 'CYCLE_REQUEST_APPROVE',
  CYCLE_REQUEST_REJECT: 'CYCLE_REQUEST_REJECT',

  // Hospital Package Request Module
  REQUEST_FOC_PACKAGES: 'REQUEST_FOC_PACKAGES',
} as const;

export type ProgramActionKey = (typeof PROGRAM_ACTION_KEYS)[keyof typeof PROGRAM_ACTION_KEYS];

// ═══════════════════════════════════════════════════════════════
// MODULE NAMES - For grouping permissions in UI
// ═══════════════════════════════════════════════════════════════

export const PERMISSION_MODULES = {
  Patient: 'Patient',
  Patient360: 'Patient360',
  Hospital: 'Hospital',
  HCP: 'HCP',
  AssistanceCase: 'AssistanceCase',
  AssistanceClaim: 'AssistanceClaim',
  CallLog: 'CallLog',
  AdverseEvent: 'AdverseEvent',
  Workflow: 'Workflow',
  Form: 'Form',
  Report: 'Report',
  Audit: 'Audit',
  Notification: 'Notification',
  Financial: 'Financial',
  Utilization: 'Utilization',
  AutomationRule: 'AutomationRule',
  Role: 'Role',
  User: 'User',
  Workspace: 'Workspace',
  CycleRequest: 'CycleRequest',
} as const;

export type PermissionModule = (typeof PERMISSION_MODULES)[keyof typeof PERMISSION_MODULES];

// ═══════════════════════════════════════════════════════════════
// PERMISSION DEFINITIONS - Complete registry
// ═══════════════════════════════════════════════════════════════

export interface PermissionDefinition {
  key: ProgramActionKey;
  module: PermissionModule;
  label: string;
  description: string;
}

export const ALL_PERMISSIONS: PermissionDefinition[] = [
  // Patient Module
  { key: 'PATIENT_VIEW', module: 'Patient', label: 'View Patients', description: 'Can view patient list and details' },
  { key: 'PATIENT_CREATE', module: 'Patient', label: 'Create Patient', description: 'Can register new patients' },
  { key: 'PATIENT_EDIT', module: 'Patient', label: 'Edit Patient', description: 'Can modify patient information' },
  { key: 'PATIENT_DELETE', module: 'Patient', label: 'Delete Patient', description: 'Can delete patients' },
  { key: 'PATIENT_VIEW_ARCHIVED', module: 'Patient', label: 'View Archived Patients', description: 'Can view archived patients in workspace and lists' },
  { key: 'PATIENT_VIEW_DELETED', module: 'Patient', label: 'View Deleted Patients', description: 'Can view deleted patients in workspace and lists' },
  { key: 'PATIENT_RESTORE_DELETED', module: 'Patient', label: 'Restore Deleted Patients', description: 'Can restore deleted patients back to active status' },
  { key: 'PATIENT_EXPORT', module: 'Patient', label: 'Export Patients', description: 'Can export patient data' },
  { key: 'PATIENT_APPROVAL_EMAIL_RESEND', module: 'Patient', label: 'Resend Patient Approval Email', description: 'Can resend pending HCP approval email for a patient' },

  // Patient 360 Module
  { key: 'PATIENT_360_VIEW', module: 'Patient360', label: 'View Patient Details', description: 'Can view patient details dashboard' },
  { key: 'PATIENT_360_VIEW_CASES', module: 'Patient360', label: 'View Patient Enrollment Cases', description: 'Can view patient\'s assistance enrollment cases' },
  { key: 'PATIENT_360_VIEW_CALL_LOGS', module: 'Patient360', label: 'View Patient Call Logs', description: 'Can view patient\'s call logs' },
  { key: 'PATIENT_360_VIEW_ADVERSE_EVENTS', module: 'Patient360', label: 'View Patient Adverse Events', description: 'Can view patient\'s adverse events' },
  { key: 'PATIENT_360_VIEW_DOCUMENTS', module: 'Patient360', label: 'View Patient Documents', description: 'Can view patient\'s documents' },
  { key: 'PATIENT_360_VIEW_TIMELINE', module: 'Patient360', label: 'View Patient Timeline', description: 'Can view patient\'s activity timeline' },

  // Hospital Module
  { key: 'HOSPITAL_VIEW', module: 'Hospital', label: 'View Hospitals', description: 'Can view hospital list and details' },
  { key: 'HOSPITAL_CREATE', module: 'Hospital', label: 'Create Hospital', description: 'Can register new hospitals' },
  { key: 'HOSPITAL_EDIT', module: 'Hospital', label: 'Edit Hospital', description: 'Can modify hospital information' },
  { key: 'HOSPITAL_DELETE', module: 'Hospital', label: 'Delete Hospital', description: 'Can delete hospitals' },
  { key: 'HOSPITAL_EXPORT', module: 'Hospital', label: 'Export Hospitals', description: 'Can export hospital data' },

  // HCP Module
  { key: 'HCP_VIEW', module: 'HCP', label: 'View HCPs', description: 'Can view HCP list and details' },
  { key: 'HCP_CREATE', module: 'HCP', label: 'Create HCP', description: 'Can register new HCPs' },
  { key: 'HCP_EDIT', module: 'HCP', label: 'Edit HCP', description: 'Can modify HCP information' },
  { key: 'HCP_DELETE', module: 'HCP', label: 'Delete HCP', description: 'Can delete HCPs' },
  { key: 'HCP_EXPORT', module: 'HCP', label: 'Export HCPs', description: 'Can export HCP data' },

  // Assistance Case Module
  { key: 'ASSISTANCE_CASE_VIEW', module: 'AssistanceCase', label: 'View Enrollment Cases', description: 'Can view assistance enrollment cases' },
  { key: 'ASSISTANCE_CASE_CREATE', module: 'AssistanceCase', label: 'Create Enrollment', description: 'Can create new assistance enrollments' },
  { key: 'ASSISTANCE_CASE_EDIT', module: 'AssistanceCase', label: 'Edit Enrollment', description: 'Can modify assistance enrollments' },
  { key: 'ASSISTANCE_CASE_CANCEL', module: 'AssistanceCase', label: 'Cancel Enrollment', description: 'Can cancel assistance enrollments' },
  { key: 'ASSISTANCE_CASE_APPROVE', module: 'AssistanceCase', label: 'Approve Enrollment', description: 'Can approve assistance enrollments' },
  { key: 'ASSISTANCE_CASE_REJECT', module: 'AssistanceCase', label: 'Reject Enrollment', description: 'Can reject assistance enrollments' },
  { key: 'ASSISTANCE_CASE_EXPORT', module: 'AssistanceCase', label: 'Export Enrollment Cases', description: 'Can export enrollment data' },

  // Assistance Claim Module
  { key: 'ASSISTANCE_CLAIM_VIEW', module: 'AssistanceClaim', label: 'View Claims', description: 'Can view assistance claims' },
  { key: 'ASSISTANCE_CLAIM_CREATE', module: 'AssistanceClaim', label: 'Create Claim', description: 'Can create new claims' },
  { key: 'ASSISTANCE_CLAIM_EDIT', module: 'AssistanceClaim', label: 'Edit Claim', description: 'Can modify claims' },
  { key: 'ASSISTANCE_CLAIM_SUBMIT', module: 'AssistanceClaim', label: 'Submit Claim', description: 'Can submit claims for approval' },
  { key: 'ASSISTANCE_CLAIM_APPROVE', module: 'AssistanceClaim', label: 'Approve Claim', description: 'Can approve claims' },
  { key: 'ASSISTANCE_CLAIM_REJECT', module: 'AssistanceClaim', label: 'Reject Claim', description: 'Can reject claims' },
  { key: 'ASSISTANCE_CLAIM_EXPORT', module: 'AssistanceClaim', label: 'Export Claims', description: 'Can export claim data' },

  // Call Log Module
  { key: 'CALL_LOG_VIEW', module: 'CallLog', label: 'View Call Logs', description: 'Can view all call logs' },
  { key: 'CALL_LOG_CREATE', module: 'CallLog', label: 'Create Call Log', description: 'Can create new call logs' },
  { key: 'CALL_LOG_EDIT', module: 'CallLog', label: 'Edit Call Log', description: 'Can modify call logs' },
  { key: 'CALL_LOG_DELETE', module: 'CallLog', label: 'Delete Call Log', description: 'Can delete call logs' },
  { key: 'CALL_LOG_ANALYTICS_VIEW', module: 'CallLog', label: 'View Call Analytics', description: 'Can view call log analytics' },
  { key: 'CALL_LOG_FOLLOWUP_WORKLIST_VIEW', module: 'CallLog', label: 'View Follow-up Worklist', description: 'Can view follow-up worklist' },
  { key: 'CALL_LOG_EXPORT', module: 'CallLog', label: 'Export Call Logs', description: 'Can export call log data' },
  { key: 'CALL_LOG_CREATE_PATIENT', module: 'CallLog', label: 'Create Patient Call Log', description: 'Can create call logs for patients' },
  { key: 'CALL_LOG_CREATE_HCP', module: 'CallLog', label: 'Create HCP Call Log', description: 'Can create call logs for HCPs' },
  { key: 'CALL_LOG_CREATE_HOSPITAL', module: 'CallLog', label: 'Create Hospital Call Log', description: 'Can create call logs for hospitals' },
  { key: 'CALL_LOG_CREATE_PHARMA', module: 'CallLog', label: 'Create Pharma Call Log', description: 'Can create call logs for pharma contacts' },

  // Adverse Event Module
  { key: 'ADVERSE_EVENT_VIEW', module: 'AdverseEvent', label: 'View Adverse Events', description: 'Can view adverse events' },
  { key: 'ADVERSE_EVENT_CREATE', module: 'AdverseEvent', label: 'Create Adverse Event', description: 'Can report new adverse events' },
  { key: 'ADVERSE_EVENT_EDIT', module: 'AdverseEvent', label: 'Edit Adverse Event', description: 'Can modify adverse events' },
  { key: 'ADVERSE_EVENT_SUBMIT', module: 'AdverseEvent', label: 'Submit Adverse Event', description: 'Can submit adverse events' },
  { key: 'ADVERSE_EVENT_REVIEW', module: 'AdverseEvent', label: 'Review Adverse Event', description: 'Can review adverse events' },
  { key: 'ADVERSE_EVENT_EXPORT', module: 'AdverseEvent', label: 'Export Adverse Events', description: 'Can export adverse event data' },

  // Workflow Module
  { key: 'WORKFLOW_VIEW', module: 'Workflow', label: 'View Workflow', description: 'Can view workflow configuration' },
  { key: 'WORKFLOW_CREATE', module: 'Workflow', label: 'Create Workflow', description: 'Can create new workflows' },
  { key: 'WORKFLOW_EDIT', module: 'Workflow', label: 'Edit Workflow', description: 'Can modify workflows' },
  { key: 'WORKFLOW_ACTIVATE', module: 'Workflow', label: 'Activate Workflow', description: 'Can activate workflows' },
  { key: 'WORKFLOW_DEACTIVATE', module: 'Workflow', label: 'Deactivate Workflow', description: 'Can deactivate workflows' },
  { key: 'WORKFLOW_CLONE', module: 'Workflow', label: 'Clone Workflow', description: 'Can clone workflows' },
  { key: 'WORKFLOW_TRANSITION', module: 'Workflow', label: 'Transition Workflow', description: 'Can execute workflow transitions at runtime' },
  { key: 'WORKFLOW_APPROVE', module: 'Workflow', label: 'Approve Workflow Actions', description: 'Can approve or reject workflow-driven actions at runtime' },

  // Form Module
  { key: 'FORM_VIEW', module: 'Form', label: 'View Forms', description: 'Can view form list and details' },
  { key: 'FORM_CREATE', module: 'Form', label: 'Create Form', description: 'Can create new forms' },
  { key: 'FORM_EDIT', module: 'Form', label: 'Edit Form', description: 'Can modify forms' },
  { key: 'FORM_PUBLISH', module: 'Form', label: 'Publish Form', description: 'Can publish forms' },
  { key: 'FORM_ARCHIVE', module: 'Form', label: 'Archive Form', description: 'Can archive forms' },
  { key: 'FORM_DELETE', module: 'Form', label: 'Delete Form', description: 'Can delete forms' },
  { key: 'FORM_CLONE', module: 'Form', label: 'Clone Form', description: 'Can clone forms' },
  { key: 'FORM_FILL', module: 'Form', label: 'Fill Forms', description: 'Can access workflow-resolved runtime forms' },
  { key: 'FORM_SUBMIT', module: 'Form', label: 'Submit Forms', description: 'Can submit workflow-resolved forms' },

  // Report Module
  { key: 'REPORT_VIEW', module: 'Report', label: 'View Reports', description: 'Can view reports and analytics' },
  { key: 'REPORT_EXPORT', module: 'Report', label: 'Export Reports', description: 'Can export report data' },
  { key: 'REPORT_SCHEDULE', module: 'Report', label: 'Schedule Reports', description: 'Can schedule automated reports' },

  // Audit Module
  { key: 'AUDIT_VIEW', module: 'Audit', label: 'View Audit Logs', description: 'Can view audit logs' },
  { key: 'AUDIT_EXPORT', module: 'Audit', label: 'Export Audit Logs', description: 'Can export audit logs' },

  // Notification Module
  { key: 'NOTIFICATION_VIEW', module: 'Notification', label: 'View Notifications', description: 'Can view notification rules' },
  { key: 'NOTIFICATION_CREATE', module: 'Notification', label: 'Create Notification', description: 'Can create notification rules' },
  { key: 'NOTIFICATION_EDIT', module: 'Notification', label: 'Edit Notification', description: 'Can modify notification rules' },
  { key: 'NOTIFICATION_DELETE', module: 'Notification', label: 'Delete Notification', description: 'Can delete notification rules' },

  // Financial Module
  { key: 'FINANCIAL_VIEW', module: 'Financial', label: 'View Financial', description: 'Can view financial settings' },
  { key: 'FINANCIAL_MANAGE', module: 'Financial', label: 'Manage Financial', description: 'Can manage financial settings' },
  { key: 'FINANCIAL_APPROVE', module: 'Financial', label: 'Approve Financial', description: 'Can approve financial changes' },

  // Utilization Module
  { key: 'UTILIZATION_VIEW', module: 'Utilization', label: 'View Utilization', description: 'Can view utilization records' },
  { key: 'UTILIZATION_CREATE', module: 'Utilization', label: 'Create Utilization', description: 'Can create utilization records' },
  { key: 'UTILIZATION_EDIT', module: 'Utilization', label: 'Edit Utilization', description: 'Can modify utilization records' },
  { key: 'UTILIZATION_SUBMIT', module: 'Utilization', label: 'Submit Utilization', description: 'Can submit utilization records' },
  { key: 'UTILIZATION_APPROVE', module: 'Utilization', label: 'Approve Utilization', description: 'Can approve utilization' },
  { key: 'UTILIZATION_REJECT', module: 'Utilization', label: 'Reject Utilization', description: 'Can reject utilization' },

  // Automation Rules Module
  { key: 'AUTOMATION_RULE_VIEW', module: 'AutomationRule', label: 'View Automation Rules', description: 'Can view automation rules' },
  { key: 'AUTOMATION_RULE_CREATE', module: 'AutomationRule', label: 'Create Automation Rule', description: 'Can create automation rules' },
  { key: 'AUTOMATION_RULE_EDIT', module: 'AutomationRule', label: 'Edit Automation Rule', description: 'Can modify automation rules' },
  { key: 'AUTOMATION_RULE_ACTIVATE', module: 'AutomationRule', label: 'Activate Automation Rule', description: 'Can activate automation rules' },
  { key: 'AUTOMATION_RULE_DEACTIVATE', module: 'AutomationRule', label: 'Deactivate Automation Rule', description: 'Can deactivate automation rules' },

  // Role Module
  { key: 'ROLE_VIEW', module: 'Role', label: 'View Roles', description: 'Can view role list' },
  { key: 'ROLE_CREATE', module: 'Role', label: 'Create Role', description: 'Can create new roles' },
  { key: 'ROLE_EDIT', module: 'Role', label: 'Edit Role', description: 'Can modify roles' },
  { key: 'ROLE_DELETE', module: 'Role', label: 'Delete Role', description: 'Can delete roles' },

  // User Module
  { key: 'USER_VIEW', module: 'User', label: 'View Users', description: 'Can view user list' },
  { key: 'USER_ASSIGN', module: 'User', label: 'Assign User', description: 'Can assign users to roles' },
  { key: 'USER_UNASSIGN', module: 'User', label: 'Unassign User', description: 'Can remove user assignments' },

  // Workspace Module
  { key: 'WORKSPACE_VIEW', module: 'Workspace', label: 'View Workspace', description: 'Can access workspace' },
  { key: 'WORKSPACE_VIEW_CASES', module: 'Workspace', label: 'View Workspace Enrollment Cases', description: 'Can view enrollment cases in workspace' },
  { key: 'WORKSPACE_VIEW_ORDERS', module: 'Workspace', label: 'View Workspace Orders', description: 'Can view orders in workspace' },
  { key: 'WORKSPACE_VIEW_APPROVALS', module: 'Workspace', label: 'View Workspace Approvals', description: 'Can view approvals in workspace' },
  { key: 'WORKSPACE_VIEW_ADVERSE_EVENTS', module: 'Workspace', label: 'View Workspace Adverse Events', description: 'Can view adverse events in workspace' },
  { key: 'WORKSPACE_VIEW_FOLLOWUPS', module: 'Workspace', label: 'View Workspace Follow-ups', description: 'Can view follow-ups in workspace' },
  { key: 'WORKSPACE_VIEW_CLAIMS', module: 'Workspace', label: 'View Workspace Claims', description: 'Can view claims in workspace' },
  { key: 'CYCLE_REQUEST_CREATE_OWN', module: 'CycleRequest', label: 'Submit Own Cycle Request', description: 'Patient can submit a treatment cycle request for their own case' },
  { key: 'CYCLE_REQUEST_CREATE', module: 'CycleRequest', label: 'Initiate Cycle Request On Behalf', description: 'Can submit a treatment cycle request on behalf of an enrolled patient' },
  { key: 'CYCLE_REQUEST_VIEW_OWN', module: 'CycleRequest', label: 'View Own Cycle Requests', description: 'Patient can view their own treatment cycle requests' },
  { key: 'CYCLE_REQUEST_VIEW', module: 'CycleRequest', label: 'View Cycle Request Queue', description: 'Can view the program cycle request queue' },
  { key: 'CYCLE_REQUEST_REVIEW', module: 'CycleRequest', label: 'Review Cycle Request', description: 'Can review and request more information on a cycle request' },
  { key: 'CYCLE_REQUEST_APPROVE', module: 'CycleRequest', label: 'Approve Cycle Request', description: 'Can approve a cycle request and set the approved assistance amount' },
  { key: 'CYCLE_REQUEST_REJECT', module: 'CycleRequest', label: 'Reject Cycle Request', description: 'Can reject a cycle request' },

  // Hospital Package Request Module
  { key: 'REQUEST_FOC_PACKAGES', module: 'Hospital', label: 'Request FOC Packages', description: 'Can submit FOC package quantity requests (FOC programs only)' },
];

// ═══════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════

/**
 * Get permissions grouped by module
 */
export function getPermissionsByModule(): Record<PermissionModule, PermissionDefinition[]> {
  return ALL_PERMISSIONS.reduce((acc, perm) => {
    if (!acc[perm.module]) {
      acc[perm.module] = [];
    }
    acc[perm.module].push(perm);
    return acc;
  }, {} as Record<PermissionModule, PermissionDefinition[]>);
}

/**
 * Get a permission definition by key
 */
export function getPermissionByKey(key: string): PermissionDefinition | undefined {
  return ALL_PERMISSIONS.find(p => p.key === key);
}

/**
 * Validate that a permission key is registered
 */
export function isValidPermissionKey(key: string): key is ProgramActionKey {
  return ALL_PERMISSIONS.some(p => p.key === key);
}

/**
 * All valid permission keys as a set for fast lookup
 */
export const ALL_PERMISSION_KEYS = new Set<string>(ALL_PERMISSIONS.map(p => p.key));

/**
 * Module display order for UI
 */
export const MODULE_DISPLAY_ORDER: PermissionModule[] = [
  'Patient',
  'Patient360',
  'Hospital',
  'HCP',
  'AssistanceCase',
  'AssistanceClaim',
  'CallLog',
  'AdverseEvent',
  'Workspace',
  'Workflow',
  'Form',
  'Report',
  'Audit',
  'Notification',
  'Financial',
  'Utilization',
  'AutomationRule',
  'Role',
  'User',
];

/**
 * Human-readable module labels
 */
export const MODULE_LABELS: Record<PermissionModule, string> = {
  Patient: 'Patients',
  Patient360: 'Patient Details',
  Hospital: 'Hospitals',
  HCP: 'HCPs',
  AssistanceCase: 'Assistance Cases',
  AssistanceClaim: 'Assistance Claims',
  CallLog: 'Call Logs',
  AdverseEvent: 'Adverse Events',
  Workflow: 'Workflow',
  Form: 'Forms',
  Report: 'Reports',
  Audit: 'Audit',
  Notification: 'Notifications',
  Financial: 'Financial',
  Utilization: 'Utilization',
  AutomationRule: 'Automation Rules',
  Role: 'Roles',
  User: 'Users',
  Workspace: 'Workspace',
  CycleRequest: 'Cycle Requests',
};
